import React, { useState, useEffect, useRef } from 'react';
import { X, MapPin, Loader2 } from 'lucide-react';
import { 
  Command, 
  CommandEmpty, 
  CommandGroup, 
  CommandList,
  CommandSeparator
} from '@/components/ui/command';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { LocationSuggestion } from '@/services/locationService';
import { StoredLocation } from '@/utils/locationUtils';
import { useLocationSuggestions } from './hooks/useLocationSuggestions';
import { useRecentLocations } from './hooks/useRecentLocations';
import { useGeolocation } from './hooks/useGeolocation';
import { CurrentLocationItem } from './components/CurrentLocationItem';
import { RecentLocationItem } from './components/RecentLocationItem';
import { SuggestionItem } from './components/SuggestionItem';
import { formatDate } from './utils/dateUtils';

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function LocationAutocomplete({ 
  value, 
  onChange, 
  placeholder = "Enter a location", 
  className 
}: LocationAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const [justSelected, setJustSelected] = useState(false);

  const { suggestions, loading } = useLocationSuggestions(inputValue);
  const { recentLocations, addRecentLocation } = useRecentLocations();
  const { getCurrentLocation, geolocating } = useGeolocation();

  // Synchronize external value with internal state
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Auto-focus input and position cursor at the end when popover opens
  useEffect(() => {
    if (open && inputRef.current) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Position cursor at the end
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
        }
      }, 10);
      
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  // Clear the input value
  const handleClear = () => {
    setInputValue('');
    onChange('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Use current location
  const handleUseCurrentLocation = async () => {
    const address = await getCurrentLocation();
    
    if (address) {
      // Update the input
      setInputValue(address);
      onChange(address);
      
      // Save to recent locations
      addRecentLocation(address);
      
      // Close the popover
      setOpen(false);
      setJustSelected(true);
      
      // Reset justSelected after a delay
      setTimeout(() => setJustSelected(false), 300);
    }
  };

  // Handle selection of a location
  const handleSelectLocation = (location: LocationSuggestion | StoredLocation) => {
    const locationText = 'description' in location ? location.description : '';
    onChange(locationText);
    setInputValue(locationText);
    setOpen(false);
    setJustSelected(true);
    
    // Save to recent locations if it's a LocationSuggestion
    if ('place_id' in location) {
      addRecentLocation(locationText);
    }
    
    // Focus back to input after selection
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
      
      // Reset justSelected after a delay to allow future selections
      setTimeout(() => {
        setJustSelected(false);
      }, 300);
    }, 10);
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (newValue.trim().length >= 2) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  // Focus handlers
  const handleFocus = () => {
    // Don't reopen the popover if we just selected an item
    if (!justSelected && inputValue.trim().length >= 2) {
      setOpen(true);
    }
  };

  return (
    <Popover 
      open={open} 
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        // When popover opens, ensure input has focus
        if (isOpen && inputRef.current) {
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
              // Position cursor at the end
              const length = inputRef.current.value.length;
              inputRef.current.setSelectionRange(length, length);
            }
          }, 10);
        }
      }}
    >
      <PopoverTrigger asChild>
        <div className="relative w-full">
          <MapPin className="absolute top-3 left-3 h-4 w-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            className={`pl-10 ${inputValue ? 'pr-8' : ''} ${className}`}
            placeholder={placeholder}
            aria-expanded={open}
            aria-autocomplete="list"
          />
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute top-3 right-3 h-4 w-4 text-muted-foreground hover:text-foreground"
              aria-label="Clear location"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </PopoverTrigger>
      
      {open && (
        <PopoverContent 
          className="p-0 w-full min-w-[300px]" 
          align="start"
          side="bottom"
          sideOffset={5}
          avoidCollisions={true}
        >
          <Command shouldFilter={false}>
            <CommandList>
              {/* Current location option */}
              <CommandGroup>
                <CurrentLocationItem 
                  onSelect={handleUseCurrentLocation}
                  geolocating={geolocating}
                />
              </CommandGroup>

              {/* Loading state */}
              {loading && (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
                </div>
              )}

              {/* No results state */}
              {!loading && suggestions.length === 0 && inputValue.trim().length >= 2 && (
                <CommandEmpty>No locations found</CommandEmpty>
              )}

              {/* Location suggestions */}
              {!loading && suggestions.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup heading="Suggestions">
                    {suggestions.map((suggestion) => (
                      <SuggestionItem
                        key={suggestion.place_id}
                        suggestion={suggestion}
                        isSelected={inputValue === suggestion.description}
                        onSelect={() => handleSelectLocation(suggestion)}
                      />
                    ))}
                  </CommandGroup>
                </>
              )}

              {/* Recent locations */}
              {recentLocations.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup heading="Recent locations">
                    {recentLocations.map((location, index) => (
                      <RecentLocationItem
                        key={`recent-${index}`}
                        location={location}
                        isSelected={inputValue === location.description}
                        onSelect={() => handleSelectLocation(location)}
                        formatDate={formatDate}
                      />
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      )}
    </Popover>
  );
}